<template>
  <div class="image-uploader-container">
    <div class="image-uploader">
      <div class="header-row">
        <div>
          <h1 class="green-heading">{{ $t('imageUploader.heading') }}</h1>
          <p class="subtitle">{{ $t('imageUploader.subtitle') }}</p>
        </div>
        <!-- TODO: when auth is wired, replace `anonymous` with the real user name from currentUser. -->
        <div class="uploading-as" :title="`${$t('imageUploader.uploadingAs')} ${$t('imageUploader.anonymous')}`">
          <span class="uploading-as-label">{{ $t('imageUploader.uploadingAs') }}</span>
          <span class="uploading-as-name">{{ $t('imageUploader.anonymous') }}</span>
        </div>
      </div>

      <p v-if="successMessage" class="banner banner-success" role="status">{{ successMessage }}</p>
      <p v-if="errorMessage" class="banner banner-error" role="alert">{{ errorMessage }}</p>

      <form class="upload-form" @submit.prevent="onSubmit" novalidate>
        <!-- 5.1 File drop zone -->
        <div
          class="file-drop"
          :class="{ 'is-dragover': isDragover, 'has-preview': !!form.file }"
          @click="onClickDrop"
          @dragover.prevent="isDragover = true"
          @dragleave.prevent="isDragover = false"
          @drop.prevent="onDrop"
        >
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            class="file-input-hidden"
            @change="onFileChange"
          />

          <!-- Empty state: prompt -->
          <div v-if="!form.file" class="file-drop-prompt">
            <div class="file-drop-icon">+</div>
            <div>{{ $t('imageUploader.dropZone.prompt') }}</div>
            <div class="file-drop-hint">{{ $t('imageUploader.dropZone.hint') }}</div>
          </div>

          <!-- Selected state: thumbnail + filename + clear button -->
          <div v-else class="file-drop-preview">
            <img v-if="previewUrl" :src="previewUrl" alt="" class="file-drop-thumb" />
            <div class="file-drop-meta">
              <div class="file-drop-selected">{{ form.file.name }}</div>
              <div class="file-drop-hint">{{ formatFileSize(form.file.size) }}</div>
              <button type="button" class="btn-link" @click.stop="clearFile">
                {{ $t('imageUploader.dropZone.change') }}
              </button>
            </div>
          </div>
        </div>

        <!-- 5.2 Three-column metadata row -->
        <div class="form-row three-col">
          <div class="form-field">
            <label :for="ids.category">
              {{ $t('imageUploader.fields.categoryLabel') }} <span class="required">*</span>
            </label>
            <select :id="ids.category" v-model="form.category" required>
              <option value="" disabled>{{ $t('imageUploader.fields.selectPlaceholder') }}</option>
              <option v-for="cat in categoryIds" :key="cat" :value="cat">
                {{ $t(`common.categories.${cat}`) }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label :for="ids.license">
              {{ $t('imageUploader.fields.licenseLabel') }} <span class="required">*</span>
            </label>
            <select :id="ids.license" v-model="form.license" required>
              <option value="" disabled>{{ $t('imageUploader.fields.selectPlaceholder') }}</option>
              <option v-for="lic in licenseIds" :key="lic" :value="lic">
                {{ $t(`imageUploader.licenses.${lic}`) }}
              </option>
            </select>
          </div>

          <div class="form-field">
            <label :for="ids.imageType">
              {{ $t('imageUploader.fields.imageTypeLabel') }} <span class="required">*</span>
            </label>
            <select :id="ids.imageType" v-model="form.imageType" required>
              <option v-for="it in imageTypeIds" :key="it" :value="it">
                {{ $t(`imageUploader.imageTypes.${it}`) }}
              </option>
            </select>
          </div>
        </div>

        <!-- 5.3 Labels fieldset -->
        <fieldset class="form-fieldset">
          <legend>
            {{ $t('imageUploader.labels.legend') }}
            <span class="required">*</span>
            <span class="hint">{{ $t('imageUploader.labels.hint') }}</span>
          </legend>
          <div class="form-row two-col">
            <div class="form-field" v-for="lang in langs" :key="`label-${lang.code}`">
              <label :for="`label-${lang.code}`">{{ lang.code }}</label>
              <input
                :id="`label-${lang.code}`"
                type="text"
                v-model="form.labels[lang.code]"
                :placeholder="lang.labelExample"
              />
            </div>
          </div>
          <p v-if="labelError" class="field-error">{{ labelError }}</p>
        </fieldset>

        <!-- 5.4 Alt text fieldset -->
        <fieldset class="form-fieldset">
          <legend>
            {{ $t('imageUploader.altText.legend') }}
            <span class="hint">{{ $t('imageUploader.altText.hint') }}</span>
          </legend>
          <div class="form-row two-col">
            <div class="form-field" v-for="lang in langs" :key="`alt-${lang.code}`">
              <label :for="`alt-${lang.code}`">{{ lang.code }}</label>
              <input :id="`alt-${lang.code}`" type="text" v-model="form.altText[lang.code]" />
            </div>
          </div>
        </fieldset>

        <!-- 5.5 Curator notes -->
        <div class="form-field">
          <label :for="ids.notes">
            {{ $t('imageUploader.curatorNotes.label') }}
            <span class="hint">{{ $t('imageUploader.curatorNotes.hint') }}</span>
          </label>
          <textarea
            :id="ids.notes"
            rows="3"
            v-model="form.notes"
            :placeholder="$t('imageUploader.curatorNotes.placeholder')"
          ></textarea>
        </div>

        <!-- 5.6 Form actions -->
        <div class="form-actions">
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            {{ submitting ? $t('imageUploader.submitting') : $t('imageUploader.submit') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { uploadImage } from '@/services/imageApi';

const CATEGORY_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const LICENSE_IDS = [1, 2, 3, 4, 5, 6, 7, 8];
const IMAGE_TYPE_IDS = [1, 2, 3, 4, 5];

// EN/DE/FR/IT codes are language identifiers (not translated). The placeholder
// example word is the native-language form of "Apple" and stays static regardless
// of the UI locale — it shows the writing style expected in that field.
const LANGS = [
  { code: 'EN', key: 'en', labelExample: 'e.g. Apple' },
  { code: 'DE', key: 'de', labelExample: 'z.B. Apfel' },
  { code: 'FR', key: 'fr', labelExample: 'ex. Pomme' },
  { code: 'IT', key: 'it', labelExample: 'es. Mela' },
];

function defaultFormState() {
  return {
    file: null,
    category: '',
    license: '',
    imageType: 1, // Photo
    labels: { EN: '', DE: '', FR: '', IT: '' },
    altText: { EN: '', DE: '', FR: '', IT: '' },
    notes: '',
  };
}

export default {
  name: 'ImageUploader',
  data() {
    return {
      form: defaultFormState(),
      isDragover: false,
      submitting: false,
      previewUrl: null,
      successMessage: null,
      errorMessage: null,
      labelError: null,
      categoryIds: CATEGORY_IDS,
      licenseIds: LICENSE_IDS,
      imageTypeIds: IMAGE_TYPE_IDS,
      langs: LANGS.map(l => ({ code: l.code, labelExample: l.labelExample })),
      ids: {
        category: 'image-uploader-category',
        license: 'image-uploader-license',
        imageType: 'image-uploader-image-type',
        notes: 'image-uploader-notes',
      },
    };
  },
  beforeUnmount() {
    this.revokePreview();
  },
  methods: {
    onClickDrop() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.setSelectedFile(file);
    },
    onDrop(e) {
      this.isDragover = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        this.setSelectedFile(file);
      }
    },
    setSelectedFile(file) {
      this.revokePreview();
      this.form.file = file;
      this.previewUrl = URL.createObjectURL(file);
    },
    clearFile() {
      this.revokePreview();
      this.form.file = null;
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    revokePreview() {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
    },
    formatFileSize(bytes) {
      if (bytes == null) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    hasAtLeastOneLabel() {
      return Object.values(this.form.labels).some(v => v.trim() !== '');
    },
    async onSubmit() {
      this.successMessage = null;
      this.errorMessage = null;
      this.labelError = null;

      // Native validation for required selects
      const formEl = this.$el.querySelector('form');
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }

      // Custom: file must be selected (drop zone is outside the native required attribute)
      if (!this.form.file) {
        this.errorMessage = this.$t('imageUploader.validation.fileRequired');
        return;
      }

      // Custom: at least one label
      if (!this.hasAtLeastOneLabel()) {
        this.labelError = this.$t('imageUploader.validation.atLeastOneLabel');
        return;
      }

      this.submitting = true;
      try {
        const created = await uploadImage(this.form.file, {
          category_id: Number(this.form.category),
          license_id: Number(this.form.license),
          image_type_id: Number(this.form.imageType),
          label_en: this.form.labels.EN,
          label_de: this.form.labels.DE,
          label_fr: this.form.labels.FR,
          label_it: this.form.labels.IT,
          alt_text_en: this.form.altText.EN,
          alt_text_de: this.form.altText.DE,
          alt_text_fr: this.form.altText.FR,
          alt_text_it: this.form.altText.IT,
          curator_notes: this.form.notes,
        });
        this.successMessage = this.$t('imageUploader.successCreated', {
          cardId: `#${String(created.id).padStart(4, '0')}`,
        });
        this.form = defaultFormState();
        this.revokePreview();
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
      } catch (e) {
        // axios error: prefer the server's `detail` message over the bare HTTP one.
        const detail = e?.response?.data?.detail;
        const msg = detail || e?.message || 'Upload failed';
        this.errorMessage = this.$t('imageUploader.errorUpload', { message: msg });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
.image-uploader-container {
  padding: 20px;
  font-family: 'Montserrat', 'Avenir', Helvetica, Arial, sans-serif;
}

.image-uploader {
  max-width: 1000px;
  margin: 0 auto;
}

.green-heading {
  color: #42b983;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.subtitle {
  color: #6c757d;
  font-size: 14px;
  margin: 0 0 1.5rem 0;
}

/* --- Header row --- */

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.uploading-as {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
  font-size: 13px;
}

.uploading-as-label {
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.uploading-as-name {
  color: #2c3e50;
  font-weight: 500;
}

/* --- Banners --- */

.banner {
  margin: 0 0 1rem 0;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 14px;
}

.banner-success {
  background: #D4EDDA;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.banner-error {
  background: #F8D7DA;
  color: #721C24;
  border: 1px solid #f5c6cb;
}

/* --- Form --- */

.upload-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* File drop zone */

.file-drop {
  position: relative;
  min-height: 200px;
  border: 2px dashed #ced4da;
  border-radius: 10px;
  background: #f8f9fa;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.file-drop:hover,
.file-drop.is-dragover {
  background: #eafaf1;
  border-color: #42b983;
}

.file-drop.has-preview {
  border-style: solid;
  border-color: #ced4da;
  background: #fff;
  cursor: default;
}

.file-drop.has-preview:hover {
  background: #fff;
  border-color: #42b983;
}

.file-input-hidden {
  display: none;
}

.file-drop-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #6c757d;
  text-align: center;
  padding: 0 16px;
}

.file-drop-icon {
  font-size: 44px;
  font-weight: 300;
  line-height: 1;
  color: #adb5bd;
}

.file-drop-hint {
  font-size: 12px;
  color: #adb5bd;
}

.file-drop-selected {
  font-weight: 500;
  color: #2c3e50;
  word-break: break-all;
}

/* Selected-file preview state */

.file-drop-preview {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.file-drop-thumb {
  width: 140px;
  height: 140px;
  object-fit: cover;
  border-radius: 8px;
  background: #f8f9fa;
  flex: 0 0 140px;
  border: 1px solid #e9ecef;
}

.file-drop-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 auto;
  min-width: 0;
}

.file-drop-meta .btn-link {
  background: none;
  border: none;
  padding: 0;
  margin-top: 4px;
  color: #6c757d;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  align-self: flex-start;
}
.file-drop-meta .btn-link:hover {
  color: #e74c3c;
}

/* Form rows */

.form-row {
  display: grid;
  gap: 16px;
}

.form-row.three-col {
  grid-template-columns: repeat(3, 1fr);
}

.form-row.two-col {
  grid-template-columns: repeat(2, 1fr);
}

/* Form fields (label above control) */

.form-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-field label {
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.form-field select,
.form-field input[type="text"],
.form-field textarea {
  font-family: inherit;
  font-size: 14px;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  background-color: #fff;
  color: #2c3e50;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-sizing: border-box;
  width: 100%;
}

.form-field select,
.form-field input[type="text"] {
  height: 38px;
}

.form-field textarea {
  resize: vertical;
  min-height: 70px;
}

.form-field select:focus,
.form-field input:focus,
.form-field textarea:focus {
  outline: none;
  border-color: #42b983;
  box-shadow: 0 0 0 3px rgba(66, 185, 131, 0.18);
}

/* Fieldsets */

.form-fieldset {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 12px 16px 16px 16px;
  margin: 0;
}

.form-fieldset legend {
  padding: 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.required {
  color: #e74c3c;
  font-weight: 700;
  margin-left: 2px;
}

.hint {
  color: #6c757d;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  margin-left: 6px;
}

.field-error {
  margin: 8px 0 0 0;
  color: #721C24;
  font-size: 13px;
}

/* Actions */

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  letter-spacing: 0.3px;
}

.btn-primary {
  background: #42b983;
  color: #fff;
}

.btn-primary:hover {
  background: #36a273;
}

.btn-primary:focus-visible {
  outline: 2px solid #2E7D32;
  outline-offset: 2px;
}

/* Responsive */

@media (max-width: 600px) {
  .form-row.three-col,
  .form-row.two-col {
    grid-template-columns: 1fr;
  }

  .header-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .uploading-as {
    align-items: flex-start;
    text-align: left;
  }

  .form-actions {
    justify-content: stretch;
  }

  .btn-primary {
    width: 100%;
  }
}
</style>
